import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import {
  CbhIcon,
  type CbhIconProps,
  ListItem,
  ListItemText,
  Text,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import pluralize from "pluralize";
import { useMemo, useState } from "react";

import { Button } from "../../components/Button";
import { type ShiftSlotCoalesced } from "../../Shift/Open/useGetShiftsSlotV2";
import { type WorkerPublicProfile } from "../hooks/useGetWorkerPublicProfile";
import { useIsWorkWithFriendsV2Enabled } from "../hooks/useIsWorkWithFriendsV2Enabled";
import AddFriendBottomSheet from "../modals/AddFriend/AddFriend";
import WorkingThisShiftWorker from "./WorkingThisShiftWorker";

interface WorkingThisShiftListProps {
  shiftSlots?: ShiftSlotCoalesced;
  shiftId?: string;
  friendsMap?: Map<string, WorkerPublicProfile>;
  outboundFriendRequestsSet?: Set<string>;
  isBookable: boolean;
  title?: string;
  showAvailabilityText?: boolean;
  nameSemibold?: boolean;
  icon?: CbhIconProps["type"];
}

export function WorkingThisShiftListItem(props: WorkingThisShiftListProps) {
  const {
    shiftSlots,
    shiftId,
    friendsMap,
    outboundFriendRequestsSet,
    isBookable,
    title: titleParameter,
    showAvailabilityText: showAvailabilityTextParameter = true,
    nameSemibold,
    icon = "user-filled",
  } = props;

  const addFriendModalState = useModalState();
  const [friendId, setFriendId] = useState<string | undefined>(undefined);
  const qualificationNames = Object.keys(shiftSlots?.availableQualifications ?? {});
  const { qualificationNameToDisplayLabelMap } =
    useGetQualificationDisplayLabels(qualificationNames);
  const worker = useDefinedWorker();

  const otherWorkersShiftAssignments = useMemo(() => {
    return shiftSlots?.shiftAssignments?.filter(
      (shiftAssignment) =>
        shiftAssignment.workerId !== worker.userId &&
        (!isDefined(shiftId) || shiftAssignment.shiftId !== shiftId)
    );
  }, [shiftSlots?.shiftAssignments, worker.userId, shiftId]);

  const availabilityText = useMemo(() => {
    const availableQualifications = Object.entries(shiftSlots?.availableQualifications ?? {});

    const friendsCount =
      otherWorkersShiftAssignments?.reduce((accumulator, shiftAssignment) => {
        if (friendsMap?.has(shiftAssignment.workerId)) {
          return accumulator + 1;
        }

        return accumulator;
      }, 0) ?? 0;

    let availabilityText = "";

    if (friendsCount > 0) {
      availabilityText += `${friendsCount} ${pluralize("friend", friendsCount)} working.`;
      availabilityText += " ";
    }

    if (availableQualifications.length > 0) {
      availabilityText += `Looking for ${availableQualifications.reduce(
        (qualificationText, [qualification, count], index) => {
          let result = qualificationText;
          if (index === availableQualifications.length - 1 && availableQualifications.length > 1) {
            result += " and ";
          }

          const displayLabel =
            qualificationNameToDisplayLabelMap.get(qualification) ?? qualification;

          let pluralizedQualification = pluralize(displayLabel, count);

          if (count > 1 && pluralizedQualification.endsWith("S")) {
            pluralizedQualification = `${pluralizedQualification.slice(0, -1)}s`;
          }

          result +=
            index === 0
              ? `${count} more ${pluralizedQualification}`
              : `${count} ${pluralizedQualification}`;

          if (index < availableQualifications.length - 2) {
            result += ", ";
          }

          return result;
        },
        ""
      )}`;
    }

    return availabilityText ?? undefined;
  }, [
    shiftSlots?.availableQualifications,
    otherWorkersShiftAssignments,
    friendsMap,
    qualificationNameToDisplayLabelMap,
  ]);

  const showAvailabilityText =
    showAvailabilityTextParameter &&
    isDefined(availabilityText) &&
    ((isDefined(otherWorkersShiftAssignments) && otherWorkersShiftAssignments.length > 0) ||
      isBookable);

  function getTitle() {
    if (isDefined(titleParameter)) {
      return titleParameter;
    }

    if (isDefined(otherWorkersShiftAssignments) && otherWorkersShiftAssignments.length > 0) {
      return "Others working this shift";
    }

    if (isBookable) {
      return "Book this shift before it's gone";
    }

    return availabilityText;
  }

  const isWorkWithFriendsV2Enabled = useIsWorkWithFriendsV2Enabled();

  if (!isWorkWithFriendsV2Enabled) {
    return null;
  }

  return (
    <ListItem>
      <ListItemText
        disableTypography
        primary={
          <Stack direction="row" gap={3} width="100%">
            <Stack flexShrink={0}>
              <CbhIcon type={icon} />
            </Stack>
            <Stack flex={1} minWidth={0}>
              <Text
                semibold
                variant="h6"
                sx={(theme) => ({
                  color: theme.palette.text.primary,
                })}
                mb={2}
              >
                {getTitle()}
              </Text>
              <Stack>
                {showAvailabilityText && (
                  <Text
                    variant="body2"
                    sx={(theme) => ({
                      color: theme.palette.text.secondary,
                    })}
                  >
                    {availabilityText}
                  </Text>
                )}
                {isDefined(otherWorkersShiftAssignments) &&
                  otherWorkersShiftAssignments.length > 0 && (
                    <Stack sx={{ mt: 6 }}>
                      {otherWorkersShiftAssignments.map((shiftAssignment) => {
                        const isFriend =
                          isDefined(friendsMap) && friendsMap.has(shiftAssignment.workerId);
                        const outboundFriendRequest =
                          isDefined(outboundFriendRequestsSet) &&
                          outboundFriendRequestsSet.has(shiftAssignment.workerId);
                        const isAddButtonDisabled = outboundFriendRequest || isFriend;
                        let addButtonText = "Add";
                        if (outboundFriendRequest) {
                          addButtonText = "Requested";
                        } else if (isFriend) {
                          addButtonText = "Friends";
                        }

                        return (
                          <Stack key={shiftAssignment.workerId} mb={6}>
                            <WorkingThisShiftWorker
                              shiftAssignment={shiftAssignment}
                              nameSemibold={nameSemibold}
                              actions={
                                <Button
                                  disabled={isAddButtonDisabled}
                                  variant={isFriend ? "outlined" : undefined}
                                  size="small"
                                  onClick={() => {
                                    setFriendId(shiftAssignment.workerId);
                                    addFriendModalState.openModal();
                                  }}
                                >
                                  {addButtonText}
                                </Button>
                              }
                            />
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}
              </Stack>
            </Stack>
          </Stack>
        }
      />

      {isDefined(friendId) && (
        <AddFriendBottomSheet
          modalState={addFriendModalState}
          friendId={friendId}
          onClose={() => {
            addFriendModalState.closeModal();
            setFriendId(undefined);
          }}
        />
      )}
    </ListItem>
  );
}
